import { format } from 'date-fns';
import { getFieldErrors, isApiError } from '@/services/api';
import type { PendingFundingApproval } from '../types/agreements.types';

export const SELECTION_CHANGED_REASON =
    'The selection changed after the request was sent, so the code no longer applies. Ask an approver again.';

export const REQUEST_APPROVAL_REASON = 'Ask an approver to authorise this excess before submitting.';

export const ENTER_CODE_REASON = 'Enter the code the approver gave you before submitting.';

const RATE_LIMITED_MESSAGE = 'Too many requests. No code was sent. Wait a few minutes and try again.';
const EMAIL_FAILED_MESSAGE = 'The approval email could not be sent, so no code was issued. Try again.';
const REJECTED_MESSAGE = 'This selection could not be put forward for approval. No code was sent.';
const GENERIC_FAILURE_MESSAGE = 'The approval request failed. No code was sent. Try again.';

export interface FundingRequestFailure {
    fieldMessage: string | null;
    notice: string | null;
}

export function fundingRequestFailure(error: unknown): FundingRequestFailure {
    const fieldMessage = getFieldErrors(error).approver_email?.[0] ?? null;
    if (fieldMessage) return { fieldMessage, notice: null };

    const status = isApiError(error) ? error.status : undefined;
    const notices: Record<number, string> = { 422: REJECTED_MESSAGE, 429: RATE_LIMITED_MESSAGE, 502: EMAIL_FAILED_MESSAGE };
    return { fieldMessage: null, notice: (status && notices[status]) || GENERIC_FAILURE_MESSAGE };
}

export function approvalSentText(approverName: string, approverEmail: string, expiresAt: string): string {
    const expiry = new Date(expiresAt);
    const until = Number.isNaN(expiry.getTime()) ? '' : ` It expires at ${format(expiry, 'HH:mm')}.`;
    const who = approverName ? `${approverName} (${approverEmail})` : approverEmail;
    return `A code was sent to ${who}. Waiting for the code.${until}`;
}

export function codeEntryHint(pending: PendingFundingApproval, resentAt: Date | null): string {
    return resentAt
        ? resentCodeHint(pending.approverName, pending.approverEmail, resentAt)
        : approvalSentText(pending.approverName, pending.approverEmail, pending.expiresAt);
}

export function resentCodeHint(approverName: string, approverEmail: string, sentAt: Date): string {
    const who = approverName || approverEmail;
    return `A new code was sent to ${who} at ${format(sentAt, 'HH:mm')}. Earlier codes no longer work here; ask ${who} to use the latest email.`;
}

export const CHECK_CODE_REASON = 'Check the approval code before submitting.';

export const CODE_ACCEPTED_MESSAGE = 'Approval code accepted. Complete the invoice details below.';

export const CODE_CHECK_FAILED_MESSAGE = 'Unable to check the code. Try again.';
